import { FC, useMemo } from 'react';
import { useTranslation } from 'src/hooks/use-translation';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material';

import LockIcon from 'icons/monochrome/lock.svg';
import { Platform, SubscriptionStatus } from 'src/types';
import { RESOURCES, usePermissions } from 'src/hooks/use-permissions';
import { IS_LOCALHOST, SUPPORT_CONTACT_URL } from 'src/config-global';
import { useNavigate } from 'src/hooks/use-navigate';
import { useBillingCheckout } from 'src/context/billing-checkout-context';
import { useAuthContext } from 'src/auth/hooks';
import { usePlatforms } from 'src/hooks/use-platforms';
import { useFileLink } from 'src/hooks/use-file-link';
import { PlatformItem } from 'src/layouts/dashboard/platform-popover/platform-item';
import { PATHS } from 'src/routes/paths';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  status: SubscriptionStatus | null;
};

export const HardPaywallModal: FC<Props> = ({ open, status }) => {
  const theme = useTheme();

  const { t } = useTranslation('', { keyPrefix: 'hard-paywall' });
  const { canWrite } = usePermissions();
  const navigate = useNavigate();
  const getFileLink = useFileLink();
  const { openCheckout } = useBillingCheckout();

  const { platformId } = useAuthContext();
  const { data: platforms } = usePlatforms();

  const canPay = canWrite(RESOURCES.SUBSCRIPTION.MAIN);

  const otherPlatforms = useMemo(
    () => platforms.filter((platform) => platform.id !== platformId),
    [platforms, platformId]
  );

  const handlePay = () => {
    openCheckout();
  };

  const handleSupport = () => {
    window.open(SUPPORT_CONTACT_URL, '_blank', 'noopener,noreferrer');
  };

  const handleSwitchPlatform = (id: Platform['id']) => {
    navigate(`${PATHS.root}?platform_id=${id}`, { replace: true });
  };

  if (IS_LOCALHOST) return null;

  return (
    <Dialog
      open={open}
      onClose={undefined}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: { borderRadius: 3, textAlign: 'center', width: 440 } },
      }}
    >
      <DialogContent sx={{ pt: 5, pb: 2, px: 4 }}>
        <Box
          sx={{
            width: 96,
            height: 96,
            bgcolor: 'primary.lighter',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <LockIcon width={48} height={48} style={{ color: theme.palette.primary.main }} />
        </Box>

        <Typography variant="h4" color="text.primary" gutterBottom>
          {t('title')}
        </Typography>

        {status && (
          <Chip
            label={t('days-overdue', { days: status.days_overdue })}
            variant="soft"
            color="error"
            size="medium"
            sx={{ mb: 2 }}
          />
        )}

        <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
          {t('body')}
        </Typography>

        {!canPay && (
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1, fontWeight: theme.typography.fontWeightRegular }}
          >
            {t('contact-admin-note')}
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexDirection: 'column',
          px: 4,
          pb: otherPlatforms.length > 0 ? 2 : 4,
          gap: 1.5,
          pt: 1,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={!canPay}
          onClick={handlePay}
        >
          {t('pay-now')}
        </Button>

        <Button variant="text" color="inherit" size="small" fullWidth onClick={handleSupport}>
          {t('contact-support')}
        </Button>
      </DialogActions>

      {otherPlatforms.length > 0 && (
        <>
          <Divider sx={{ mx: 4 }} />

          <Stack sx={{ px: 4, pt: 2.5, pb: 1 }} alignItems="center">
            <Typography variant="subtitle2" color="text.primary">
              {t('switch-platform')}
            </Typography>
          </Stack>

          <Box sx={{ mx: 2, mt: 0.5, mb: 2, maxHeight: 160, overflowY: 'auto' }}>
            {otherPlatforms.map((platform) => (
              <MenuItem
                key={platform.id}
                onClick={() => handleSwitchPlatform(platform.id)}
                sx={{ borderRadius: 1.5, py: 1, '& .MuiStack-root': { maxWidth: 'none' } }}
              >
                <PlatformItem
                  name={platform.name}
                  role={platform.role}
                  icon={getFileLink(platform.images?.favicon, platform.bucket_name)}
                />
              </MenuItem>
            ))}
          </Box>
        </>
      )}
    </Dialog>
  );
};
